
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { format, addHours, isBefore, isAfter, parseISO } from 'date-fns';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  read: boolean;
  createdAt: string;
  link?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => void;
  refreshNotifications: () => Promise<void>;
}

// Create the notification context
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Separate hook to get user info directly from auth library to avoid circular dependency
const useAuthUser = () => {
  const [user, setUser] = useState<any>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        setUser(data.user);
      } catch (error) {
        console.error("Error getting auth user:", error);
      } finally {
        setIsInitialized(true);
      }
    };

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event, session?.user);
        setUser(session?.user || null);
        setIsInitialized(true);
      }
    );

    fetchUser();
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return { user, isInitialized };
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get user directly instead of from auth context to avoid circular dependencies
  const { user, isInitialized } = useAuthUser();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  
  // Function to load read notification IDs from localStorage
  const loadReadNotifications = (): string[] => {
    if (!user) return [];
    try {
      const stored = localStorage.getItem(`read_notifications_${user.id}`);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Error loading read notifications from localStorage:", error);
      return [];
    }
  };
  
  // Function to save read notification IDs to localStorage
  const saveReadNotifications = (readIds: string[]) => {
    if (!user) return;
    try {
      localStorage.setItem(`read_notifications_${user.id}`, JSON.stringify(readIds));
    } catch (error) {
      console.error("Error saving read notifications to localStorage:", error);
    }
  };
  
  // Helper function to create time-based notification message
  const getTimeBasedMessage = (courseTitle: string, startDate: string, startTime?: string): string => {
    const eventDate = startTime 
      ? new Date(`${startDate}T${startTime}`)
      : new Date(startDate);
    const now = new Date();
    const hoursDifference = Math.round((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (hoursDifference <= 1) {
      return `Your course "${courseTitle}" is starting in less than an hour!`;
    } else if (hoursDifference <= 24) {
      return `Your course "${courseTitle}" is starting in ${hoursDifference} hours`;
    } else {
      const days = Math.floor(hoursDifference / 24);
      return `Your course "${courseTitle}" is starting in ${days} day${days > 1 ? 's' : ''}`;
    }
  };
  
  // Function to fetch notifications data
  const fetchNotificationsData = async () => {
    if (!user) {
      setNotifications([]);
      return;
    }

    try {
      // Get user's course registrations
      const { data: registrations, error: regError } = await supabase
        .from('course_registrations')
        .select('*, courses(id, title, start_date, end_date, start_time, end_time, event_type, status, category)')
        .eq('user_id', user.id)
        .order('registration_date', { ascending: false });
          
      // Get user's certificates
      const { data: certificates, error: certError } = await supabase
        .from('certificates')
        .select('*, courses(title, category)')
        .eq('user_id', user.id)
        .order('issue_date', { ascending: false });
          
      if (regError) console.error('Error fetching registrations:', regError);
      if (certError) console.error('Error fetching certificates:', certError);
        
      const notificationsArray: Notification[] = [];
        
      // Convert registrations to notifications
      if (registrations) {
        registrations.forEach((reg: any) => {
          // Skip if course is null
          if (!reg.courses) {
            return;
          }

          // Registration notification
          if (reg.status === 'registered') {
            notificationsArray.push({
              id: `reg-${reg.id}`,
              title: 'Course Registration',
              message: `You are registered for "${reg.courses?.title}"`,
              type: 'info',
              read: false,
              createdAt: reg.registration_date,
              link: `/courses/${reg.course_id}`
            });
          }
            
          // Waitlist notification
          if (reg.status === 'waitlisted') {
            notificationsArray.push({
              id: `wait-${reg.id}`,
              title: 'Waitlist Status',
              message: `You are #${reg.waitlist_position} on the waitlist for "${reg.courses?.title}"`,
              type: 'warning',
              read: false,
              createdAt: reg.registration_date,
              link: `/courses/${reg.course_id}`
            });
          }
            
          // For upcoming courses (if live event and has start_date)
          if (reg.courses?.event_type === 'live' && reg.courses?.start_date) {
            const now = new Date();
            
            // Create a date object that includes both date and time if available
            let eventDate: Date;
            if (reg.courses.start_time) {
              eventDate = new Date(`${reg.courses.start_date}T${reg.courses.start_time}`);
            } else {
              eventDate = new Date(reg.courses.start_date);
            }
            
            // Create notifications for different time thresholds
            // 1 day before
            const oneDayBefore = addHours(eventDate, -24);
            if (isAfter(oneDayBefore, now) && isBefore(oneDayBefore, addHours(now, 1))) {
              notificationsArray.push({
                id: `upcoming-24h-${reg.id}`,
                title: 'Course Tomorrow',
                message: `Your course "${reg.courses.title}" starts tomorrow at ${
                  reg.courses.start_time 
                    ? format(parseISO(`2000-01-01T${reg.courses.start_time}`), 'h:mm a')
                    : 'scheduled time'
                }`,
                type: 'warning',
                read: false,
                createdAt: new Date().toISOString(),
                link: `/courses/${reg.course_id}`
              });
            }
            
            // 2 hours before
            const twoHoursBefore = addHours(eventDate, -2);
            if (isAfter(twoHoursBefore, now) && isBefore(twoHoursBefore, addHours(now, 1))) {
              notificationsArray.push({
                id: `upcoming-2h-${reg.id}`,
                title: 'Course Starting Soon',
                message: `Your course "${reg.courses.title}" starts in 2 hours`,
                type: 'warning',
                read: false,
                createdAt: new Date().toISOString(),
                link: `/courses/${reg.course_id}`
              });
            }
            
            // About to start (15 min before)
            const almostStarting = addHours(eventDate, -0.25);
            if (isAfter(almostStarting, now) && isBefore(almostStarting, addHours(now, 0.25))) {
              notificationsArray.push({
                id: `upcoming-15m-${reg.id}`,
                title: 'Course Starting Now',
                message: `Your course "${reg.courses.title}" is about to begin!`,
                type: 'error', // Using error for high visibility
                read: false,
                createdAt: new Date().toISOString(),
                link: `/courses/${reg.course_id}`
              });
            }
            
            // General upcoming notification (within 3 days)
            if (isAfter(eventDate, now) && isBefore(eventDate, addHours(now, 72))) {
              notificationsArray.push({
                id: `upcoming-${reg.id}`,
                title: 'Upcoming Course',
                message: getTimeBasedMessage(reg.courses.title, reg.courses.start_date, reg.courses.start_time),
                type: 'info',
                read: false,
                createdAt: new Date().toISOString(),
                link: `/courses/${reg.course_id}`
              });
            }
          }

          // For completed attendance
          if (reg.attendance_date) {
            notificationsArray.push({
              id: `attendance-${reg.id}`,
              title: 'Attendance Verified',
              message: `Your attendance for "${reg.courses.title}" has been verified`,
              type: 'success',
              read: false,
              createdAt: reg.attendance_date,
              link: `/certificates`
            });
          }
        });
      }
        
      // Convert certificates to notifications
      if (certificates) {
        certificates.forEach((cert: any) => {
          if (!cert.courses) {
            return;
          }
          
          notificationsArray.push({
            id: `cert-${cert.id}`,
            title: 'Certificate Available',
            message: `Your certificate for "${cert.courses?.title}" is ready to download`,
            type: 'success',
            read: false,
            createdAt: cert.issue_date,
            link: `/certificates`
          });
        });
      }
        
      // Sort notifications by createdAt
      notificationsArray.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      
      // Apply read status from localStorage
      const readIds = loadReadNotifications();
      const notificationsWithReadStatus = notificationsArray.map(notification => ({
        ...notification,
        read: readIds.includes(notification.id)
      }));
        
      setNotifications(notificationsWithReadStatus);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast({
        title: "Error",
        description: "Failed to load notifications",
        variant: "destructive",
      });
    }
  };
  
  // Load notifications when user logs in
  useEffect(() => {
    // Only fetch notifications when auth is initialized and we have a user
    if (isInitialized && user) {
      fetchNotificationsData();
      
      // Set up an interval to refresh notifications more frequently (every 2 minutes)
      const intervalId = setInterval(fetchNotificationsData, 120000);
      
      // Clean up interval on unmount
      return () => clearInterval(intervalId);
    }
  }, [user, isInitialized]);
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true } 
          : notification
      )
    );
    
    // Update localStorage
    const readIds = loadReadNotifications();
    if (!readIds.includes(id)) {
      saveReadNotifications([...readIds, id]);
    }
  };
  
  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
    
    // Update localStorage with all notification IDs
    const allIds = notifications.map(n => n.id);
    saveReadNotifications(allIds);
  };
  
  const addNotification = (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      read: false,
    };
    
    setNotifications(prev => [newNotification, ...prev]);
  };

  const refreshNotifications = async () => {
    await fetchNotificationsData();
  };
  
  return (
    <NotificationContext.Provider 
      value={{ 
        notifications, 
        unreadCount, 
        markAsRead, 
        markAllAsRead,
        addNotification,
        refreshNotifications 
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Export the hook to use the notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
